import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
  Alert,
  Button,
} from '@mui/material';
import {
  Pool as PoolIcon,
  Water as WaterIcon,
  PersonOutline as PersonIcon,
  Sensors as SensorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import standardPoolApi from '../../services/standardPoolApi';
import api from '../../services/api';
import {
  fetchPrimaryPoolSlots,
  PRIMARY_POOLS_UPDATED_EVENT,
} from '../../services/dashboardSettingsService';
import { buildPoolStatusCard, type PoolStatusCardModel } from '../../utils/poolStatusDisplay';
import type { Pool } from '../../types/domain';
import type { Cctv } from '../../types/domain';

interface PoolStatusWidgetProps {
  poolId?: string;
  maxItems?: number;
  title?: string;
  subtitle?: string;
  onRefresh?: () => void;
  isLoading?: boolean;
  /** true면 주요 시설 미지정 시 안내 문구 강조 */
  showEmptySettingsHint?: boolean;
  /** true면 기본 설정 무시, 등록된 모든 시설 표시 */
  showAllPools?: boolean;
}

function normalizePoolsResponse(raw: unknown): Pool[] {
  if (Array.isArray(raw)) return raw as Pool[];
  const o = raw as { pools?: Pool[]; data?: Pool[] };
  if (Array.isArray(o?.pools)) return o.pools;
  if (Array.isArray(o?.data)) return o.data;
  return [];
}

const PoolStatusWidget: React.FC<PoolStatusWidgetProps> = ({
  poolId,
  maxItems = 4,
  title = '시설 상태',
  subtitle = '주요 시설 실시간 상태',
  onRefresh,
  isLoading: externalLoading = false,
  showEmptySettingsHint = false,
  showAllPools = false,
}) => {
  const theme = useTheme();
  const [poolStatuses, setPoolStatuses] = useState<PoolStatusCardModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [primarySlots, setPrimarySlots] = useState<{ code: string; order: number }[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const slots = showAllPools ? [] : await fetchPrimaryPoolSlots();
      if (!showAllPools) {
        setPrimarySlots(slots);
      } else {
        setPrimarySlots([]);
      }

      const poolsRes = await standardPoolApi.getAllPools();
      let allPools: Pool[] =
        poolsRes.success && poolsRes.data ? poolsRes.data : [];
      if (allPools.length === 0) {
        const raw = await api.get<{ pools?: Pool[] } | Pool[]>('/api/pools');
        allPools = normalizePoolsResponse(raw);
      }

      let cctvList: Cctv[] = [];
      try {
        const cctvRaw = await api.get<Cctv[] | { data?: Cctv[] }>('/api/cctvs/');
        cctvList = Array.isArray(cctvRaw)
          ? cctvRaw
          : Array.isArray((cctvRaw as { data?: Cctv[] })?.data)
            ? (cctvRaw as { data: Cctv[] }).data
            : [];
      } catch {
        cctvList = [];
      }

      let targets: Pool[];
      if (showAllPools) {
        targets = [...allPools];
        targets.sort((a, b) =>
          String(a.name || a.code || '').localeCompare(String(b.name || b.code || ''), 'ko')
        );
      } else {
        const orderByCode = new Map(
          slots.map((s) => [String(s.code).toUpperCase(), s.order])
        );
        targets = allPools.filter((p) =>
          orderByCode.has(String(p.code || '').toUpperCase())
        );
        targets.sort((a, b) => {
          const oa = orderByCode.get(String(a.code || '').toUpperCase()) ?? 9999;
          const ob = orderByCode.get(String(b.code || '').toUpperCase()) ?? 9999;
          return oa - ob || String(a.code).localeCompare(String(b.code), 'ko');
        });
      }
      if (poolId) {
        targets = targets.filter(
          (p) => String(p.code) === poolId || String(p.id) === poolId
        );
      }

      const cards = targets.map((pool) => {
        const pid = pool.id;
        const pidNum =
          typeof pid === 'number' ? pid : pid != null && /^\d+$/.test(String(pid)) ? Number(pid) : null;
        const poolCctvs = cctvList.filter((c) => {
          if (pidNum != null && c.pool_id === pidNum) return true;
          const cPoolCode = String((c as { pool_code?: string }).pool_code || '');
          return Boolean(pool.code && cPoolCode && cPoolCode === pool.code);
        });
        const online = poolCctvs.filter((c) => {
          const s = String(c.status || '').toUpperCase();
          return s === 'ACTIVE' || s === 'ONLINE' || c.is_active;
        }).length;
        return buildPoolStatusCard(pool, {
          cctvTotal: poolCctvs.length || pool.cctvs?.length,
          cctvOnline: online,
        });
      });

      setPoolStatuses(cards);
    } catch {
      setLoadError('시설 상태를 불러오기 실패');
      setPoolStatuses([]);
    } finally {
      setLoading(false);
    }
  }, [poolId, showAllPools]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const onUpdate = () => loadData();
    window.addEventListener(PRIMARY_POOLS_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(PRIMARY_POOLS_UPDATED_EVENT, onUpdate);
  }, [loadData]);

  const handleRefresh = () => {
    onRefresh?.();
    loadData();
  };

  const busy = loading || externalLoading;
  const noPrimary = !showAllPools && primarySlots.length === 0;
  const displayList = showAllPools
    ? poolStatuses
    : poolStatuses.slice(0, maxItems);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good':
      case 'safe':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'critical':
      case 'danger':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
      case 'safe':
        return <CheckIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'critical':
      case 'danger':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Tooltip title="새로고침">
            <span>
              <IconButton onClick={handleRefresh} disabled={busy}>
                {busy ? <CircularProgress size={24} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {loadError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {loadError}
          </Alert>
        )}

        {noPrimary && (showEmptySettingsHint || poolStatuses.length === 0) && (
          <Alert
            severity="info"
            sx={{ mb: 1 }}
            action={
              <Button
                color="inherit"
                size="small"
                component={RouterLink}
                to="/central-control?tab=system"
                startIcon={<SettingsIcon />}
              >
                설정
              </Button>
            }
          >
            주요 시설이 지정되지 않음. 시스템 관리 → 기본 설정 → 주요 시설에서 표시 순번을
            지정
          </Alert>
        )}

        {busy && poolStatuses.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : poolStatuses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary">
              {noPrimary
                ? '표시할 주요 시설 없음'
                : '선택한 시설 데이터를 찾기 불가'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {displayList.map((pool) => (
              <Grid item xs={12} key={pool.code}>
                <Card
                  variant="outlined"
                  component={RouterLink}
                  to={`/pool-management/${pool.code}`}
                  sx={{
                    p: 1,
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                    '&:hover': {
                      boxShadow: 2,
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          mr: 1.5,
                          bgcolor:
                            pool.waterQuality.status === 'critical' ||
                            pool.safety.status === 'danger'
                              ? theme.palette.error.light
                              : pool.waterQuality.status === 'warning' ||
                                  pool.safety.status === 'warning'
                                ? theme.palette.warning.light
                                : theme.palette.primary.light,
                        }}
                      >
                        <PoolIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1">{pool.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pool.location} · {pool.code}
                        </Typography>
                      </Box>
                    </Box>

                    {pool.alerts.total > 0 && (
                      <Chip
                        icon={
                          pool.alerts.critical > 0 ? (
                            <ErrorIcon fontSize="small" />
                          ) : (
                            <WarningIcon fontSize="small" />
                          )
                        }
                        label={`${pool.alerts.total}개 알림`}
                        size="small"
                        color={pool.alerts.critical > 0 ? 'error' : 'warning'}
                        sx={{ height: 24 }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <WaterIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: getStatusColor(pool.waterQuality.status) }}
                        />
                        <Typography variant="body2">수질</Typography>
                      </Box>
                      <Chip
                        label={
                          pool.waterQuality.status === 'good'
                            ? '양호'
                            : pool.waterQuality.status === 'warning'
                              ? '주의'
                              : '위험'
                        }
                        size="small"
                        sx={{
                          bgcolor: `${getStatusColor(pool.waterQuality.status)}20`,
                          color: getStatusColor(pool.waterQuality.status),
                          height: 24,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {getStatusIcon(pool.safety.status)}
                        <Typography variant="body2" sx={{ ml: 0.5 }}>
                          안전
                        </Typography>
                      </Box>
                      <Chip
                        label={
                          pool.safety.status === 'safe'
                            ? '안전'
                            : pool.safety.status === 'warning'
                              ? '주의'
                              : '위험'
                        }
                        size="small"
                        sx={{
                          bgcolor: `${getStatusColor(pool.safety.status)}20`,
                          color: getStatusColor(pool.safety.status),
                          height: 24,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2">이용률</Typography>
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">
                            {pool.usage.current}/{pool.usage.capacity}
                          </Typography>
                          <Typography variant="caption" fontWeight="bold">
                            {pool.usage.percentage}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={pool.usage.percentage}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            bgcolor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                              bgcolor:
                                pool.usage.percentage > 80
                                  ? theme.palette.warning.main
                                  : theme.palette.primary.main,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <SensorIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2">CCTV</Typography>
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">
                            {pool.devices.online}/{pool.devices.total}
                          </Typography>
                          <Typography variant="caption" fontWeight="bold">
                            {pool.devices.percentage}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={pool.devices.percentage}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            bgcolor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                              bgcolor:
                                pool.devices.percentage < 70
                                  ? theme.palette.error.main
                                  : pool.devices.percentage < 90
                                    ? theme.palette.warning.main
                                    : theme.palette.success.main,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      갱신: {pool.lastUpdated}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default PoolStatusWidget;
